import { Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material'
import { ReactComponent as SlackComingSoon } from './slack-coming-soon.svg'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { ReactComponent as SlackIcon } from '../NavBar/slack.svg'
import { authenticatedUserAtom } from 'state'
import { useRecoilValue } from 'recoil'
import { API, COLORS } from 'app-constants'
import { useActiveOrganization } from 'hooks/use-organization'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useFetchTwinsListInsights } from 'hooks/use-insights'
import { myTwinAtom } from 'state/my-twin'
import InsightsCard from 'components/Insights/InsightsCard'
import { User } from 'api/model'
import CloseIcon from '@mui/icons-material/Close'

const ComingSoon = () => {
  return (
    <Stack spacing={2} justifyContent="center" alignItems="center" height="100%">
      <SlackComingSoon
        style={{
          height: '200px',
          width: '200px',
        }}
      />

      <Typography
        variant="h3.75"
        sx={{
          color: COLORS.GRAY_3,
        }}>
        New features coming soon!
      </Typography>
    </Stack>
  )
}

export const Banner = ({
  title,
  description,
  onClick,
  buttonText,
  icon,
  onDismissClicked,
}: {
  title: string
  description: string
  onClick: () => void
  buttonText: string
  icon: React.ReactNode
  onDismissClicked?: () => void
}) => {
  const isMobile = useCheckMobileScreen()

  return (
    <Stack
      p="20px"
      border="1px solid rgba(36, 103, 244, 0.20)"
      borderRadius="8px"
      direction={isMobile ? 'column' : 'row'}
      justifyContent="space-between"
      alignItems="center"
      sx={{
        background: 'linear-gradient(267deg, rgba(0, 195, 240, 0.15) 34.67%, rgba(17, 93, 252, 0.15) 95.74%)',
      }}>
      <Stack direction="row" alignItems="center" spacing="20px">
        {icon}
        <Stack spacing={1}>
          <Typography variant="h5">{title}</Typography>

          <Typography variant="pS">{description}</Typography>
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} mt={isMobile ? '16px' : 0}>
        <Button
          variant="contained"
          size="small"
          onClick={onClick}
          endIcon={
            <ArrowForwardIcon
              sx={{
                width: '16px',
                height: '16px',
                fill: COLORS.JELLED_PRIMARY_BLUE,
              }}
            />
          }
          sx={{
            color: COLORS.JELLED_PRIMARY_BLUE,
            backgroundColor: 'rgba(36, 103, 244, 0.10)',
            height: '36px',
            minWidth: 'fit-content',
            flex: 'auto',
            marginLeft: isMobile ? 0 : '42px',
            '&:hover': {
              color: COLORS.PURE_WHITE,
              '.MuiSvgIcon-root': {
                fill: COLORS.PURE_WHITE,
              },
            },
          }}>
          {buttonText}
        </Button>

        {onDismissClicked && (
          <IconButton onClick={onDismissClicked}>
            <CloseIcon
              sx={{
                width: '16px',
                height: '16px',
                fill: COLORS.GRAY_3,
              }}
            />
          </IconButton>
        )}
      </Stack>
    </Stack>
  )
}

export const AddSlackToInsightsBanner = ({ user }: { user: User | null }) => {
  const [activeOrganization] = useActiveOrganization()

  return (
    <Banner
      icon={
        <SlackIcon
          style={{
            width: '20px',
            height: '20px',
          }}
        />
      }
      title="Add Slack to your insights!"
      description="Enhance your insights across platforms adding the JelledAI app to Slack. Connecting with Slack will also allow you to get insights directly in Slack."
      buttonText="Connect"
      onClick={() => {
        const url = `${API.ORIGIN}/api/auth/oauth/slack-install?org_id=${activeOrganization}&path=/&email=${user?.email}`
        window.open(url)
      }}
    />
  )
}

export const SlackAppAvailableBanner = ({ user }: { user: User | null }) => {
  const [activeOrganization] = useActiveOrganization()

  return (
    <Banner
      icon={
        <SlackIcon
          style={{
            width: '20px',
            height: '20px',
          }}
        />
      }
      title="The Slack app is now available!"
      description="Enhance your in-app workflow by getting summaries of your favorite channels around customized topics. Never miss out on important messages again!"
      buttonText="Connect"
      onClick={() => {
        const url = `${API.ORIGIN}/api/auth/oauth/slack-install?org_id=${activeOrganization}&path=/&email=${user?.email}`
        window.open(url)
      }}
    />
  )
}

const Slack = () => {
  const isMobile = useCheckMobileScreen()
  const user = useRecoilValue(authenticatedUserAtom)
  const hasAddedSlackApp = user?.has_slack
  const isComingSoon = !user?.has_slack
  const myTwin = useRecoilValue(myTwinAtom)

  // fetch slack insights
  const { twinInsightsWithLatest, isLoading: isLoadingInsightsList } = useFetchTwinsListInsights(myTwin?.id ?? '', {
    source: 'slack',
  })

  if (isLoadingInsightsList) {
    return (
      <Stack>
        <Stack
          py="40px"
          pl="41px"
          sx={{
            backgroundColor: '#fff',
            borderBottom: '1px solid #ddd',
          }}>
          <Typography variant="h4">Slack</Typography>
        </Stack>

        <Stack width="100%" height="100%" justifyContent="center" alignItems="center">
          <CircularProgress size="24px" thickness={4} style={{ color: '#115DFC' }} />
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack>
      {!isMobile && (
        <Stack
          py="40px"
          pl="41px"
          sx={{
            backgroundColor: COLORS.PURE_WHITE,
            borderBottom: `1px solid ${COLORS.GRAY_5}`,
          }}>
          <Typography variant="h4">Slack</Typography>
        </Stack>
      )}

      <Stack height="calc(100vh - 320px)" p={3}>
        {!hasAddedSlackApp && <SlackAppAvailableBanner user={user} />}
        {isComingSoon && <ComingSoon />}
        {user?.has_slack && <InsightsCard insights={twinInsightsWithLatest?.insights ?? []} />}
      </Stack>
    </Stack>
  )
}

export default Slack
